import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { Subject, interval, throwError } from 'rxjs';
import { catchError, retry, startWith, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { BreadcrumbService } from 'src/app/service/breadcrumb.service';
import { UserService } from 'src/app/service/user.service';
import { OccurrenceService } from 'src/app/service/occurrence.service';
import { MenuItem, MessageService } from 'primeng/api';
import { EquipmentVideoFilterModel } from 'src/app/models/equipmentVideoFilter.model';
import { Contract } from 'src/app/models/user.model';
import { DeviceService } from 'src/app/service/devices.service';
import { EquipmentVideoService, ReportPageResponse, ReportRecord, SavePassageCountPayload, VideoDownloadStatus } from 'src/app/service/equipment-video.service';
import Hls from 'hls.js';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

interface LaneOption {
  id: string;
  label: string;
}

interface DeviceOption {
  deviceId: string | number;
  deviceName: string;
  equipmentIds: Array<string | number>;
}

interface GeneratedReportRow {
  id: number;
  dataHora: string;
  dataHoraInicioVideo: string;
  dataHoraFimVideo: string;
  idEquipamento: string;
  laneLabel: string;
  nomeOperador: string;
  contagemTotal: number;
  contagemEsperada: number;
  selected: boolean;
}

interface ReportColumn {
  field: keyof Omit<GeneratedReportRow, 'id' | 'selected'>;
  header: string;
}

interface UploadedVideoMetadata {
  equipmentId: string;
  laneLabel: string;
  startDate: Date;
  endDate: Date;
  durationMinutes: number;
}

@Component({
  selector: 'app-equipment-alert',
  templateUrl: './equipment-video.component.html',
  styleUrls: ['./equipment-video.component.scss']
})
export class EquipmentVideoComponent implements OnInit, OnDestroy {
  private readonly START_TIME = 'start' as const;
  private readonly END_TIME = 'end' as const;
  private readonly videoEndToleranceSeconds = 0.5;

  @ViewChild('videoPlayer')    videoPlayerRef!:    ElementRef<HTMLVideoElement>;
  @ViewChild('videoContainer') videoContainerRef!: ElementRef<HTMLElement>;

  private _destroy$: Subject<void> = new Subject<void>();

  stylePattern = { 'min-width': '100%', 'max-width': '100%', 'height': '40px' };
  loading: boolean = false;

  contracts!: Array<Contract>;
  videoFilter: EquipmentVideoFilterModel = new EquipmentVideoFilterModel();
  devices: DeviceOption[] = [];
  lanes:   LaneOption[] = [];
  maxDate: Date = new Date();
  minDate: Date = new Date();

  passageCount:   number = 0;
  lastAction:     'inc' | 'dec' | 'zero' | null = null;
  counterPulse:   boolean = false;
  counterHistory: { time: string; action: string; videoTime: number | null; count: number }[] = [];
  savingCount:    boolean = false;

  loadingLane: boolean = false;
  downloadProgress: number = 0;
  downloadedBytes: number = 0;
  downloadProgressAvailable: boolean = false;
  downloadingVideo: boolean = false;
  downloadPhase: string = '';
  downloadedBytesLabel: string = '';
  videoLoaded:    boolean = false;
  videoTimestamp: string  = '';
  isPlaying:      boolean = false;
  isFullscreen:   boolean = false;
  videoPlaybackEnded: boolean = false;
  speedOptions: number[] = [0.25, 0.5, 1, 1.25, 1.5, 2, 3, 4];
  currentSpeedIndex: number = this.speedOptions.indexOf(1);
  videoSpeed: number = 1;
  uploadedMetadataAutoFilled: boolean = false;
  appliedVideoFilterSummary: string | null = null;
  generatedReportRows: GeneratedReportRow[] = [];
  private reportRecords: ReportRecord[] = [];
  loadingReports:   boolean              = false;
  reportGenerated:  boolean              = false;
  reportColumns: ReportColumn[] = [
    { field: 'dataHora', header: 'Data/Hora' },
    { field: 'dataHoraInicioVideo', header: 'Inicio Video' },
    { field: 'dataHoraFimVideo', header: 'Fim Video' },
    { field: 'idEquipamento', header: 'Equipamento' },
    { field: 'laneLabel', header: 'Pista' },
    { field: 'nomeOperador', header: 'Operador' },
    { field: 'contagemTotal', header: 'Contagem do Operador' },
    { field: 'contagemEsperada', header: 'Contagem do Equipamento'}
  ];

  get selectedReportRows(): GeneratedReportRow[] {
    return this.generatedReportRows.filter(row => row.selected);
  }

  get areAllReportRowsSelected(): boolean {
    return this.generatedReportRows.length > 0 && this.generatedReportRows.every(row => row.selected);
  }

  toggleAllReportRows(selected: boolean): void {
    this.generatedReportRows.forEach(row => row.selected = selected);
  }

  durationOptions: { label: string; value: number }[] = [
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '20 min', value: 20 },
    { label: '25 min', value: 25 },
    { label: '30 min', value: 30 },
  ];

  private videoInterval: any = null;
  private hlsPlayer: Hls | null = null;
  private hlsSessionId: string | null = null;
  private hlsEquipmentId: string | null = null;
  private videoStreamComplete: boolean = false;
  private localVideoUrl: string | null = null;
  private uploadedVideoActive: boolean = false;
  private uploadedVideoWindow: { startDate: Date; endDate: Date } | null = null;
  private pendingUploadedLaneLabel: string | null = null;
  private downloadCancel$ = new Subject<void>();
  private activeDownloadJobId: string | null = null;

  tabItems:  MenuItem[]         = [];
  activeTab: MenuItem | undefined;

  get isUploadedVideo(): boolean {
    return this.uploadedVideoActive;
  }

  get areFiltersLocked(): boolean {
    if (this.isUploadedVideo && !this.uploadedMetadataAutoFilled) {
      return false;
    }
    return this.isUploadedVideo || this.videoLoaded || this.isPlaying;
  }

  constructor(
    private breadCrumb:            BreadcrumbService,
    private occurrenceService:     OccurrenceService,
    private userService:           UserService,
    private messageService:        MessageService,
    private deviceService:         DeviceService,
    private equipmentVideoService: EquipmentVideoService,
    private ngZone:                NgZone,
    private cdr:                   ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() - 5); // persistência de video de 5 dias
    this.breadCrumb.next([
      { label: 'Configuracoes',          icon: 'pi pi-sliders-v' },
      { label: 'Monitoramento de Vídeo', icon: 'pi pi-video' }
    ]);
    this.initTabMenu();
    this.getContract();
    this.occurrenceService.setOccurreceInUse(null);
  }

  ngOnDestroy(): void {
    this.stopVideo();
    this.revokeLocalVideoUrl();
    this.stopVideoTimestamp();
    this._destroy$.next();
    this._destroy$.complete();
    this.downloadCancel$.complete();
    this.downloadingVideo = false;
  }

  initTabMenu(): void {
    this.tabItems = [
      { label: 'Vídeo',      icon: 'pi pi-video',    id: 'video' },
      { label: 'Relatórios', icon: 'pi pi-file-edit', id: 'relatorios' }
    ];
    this.activeTab = this.tabItems[0];
  }

  onTabChange(item: MenuItem): void {
    if (this.activeTab?.id === 'video' && item.id !== 'video') {
      this.pauseVideoForTabChange();
    }

    this.activeTab = item;
    this.resetOnTabChange();
  }


  private resetOnTabChange(): void {
    this.stopVideo();
    this.revokeLocalVideoUrl();
    this.uploadedVideoActive = false;
    this.uploadedMetadataAutoFilled = false;
    this.uploadedVideoWindow = null;
    this.appliedVideoFilterSummary = null;
    this.pendingUploadedLaneLabel = null;
    this.videoLoaded = false;
    this.isPlaying = false;
    this.savingCount = false;
    this.stopVideoTimestamp();

    this.passageCount = 0;
    this.lastAction = null;
    this.counterHistory = [];

    this.generatedReportRows = [];
    this.reportRecords = [];
    this.reportGenerated = false;

    const startHour = new Date();
    startHour.setHours(0, 0, 0, 0);
    this.videoFilter.startHour = startHour;

    const endHour = new Date();
    endHour.setHours(23, 59, 59, 0);
    this.videoFilter.endHour = endHour;
  
    this.enforceReportDateTimeConstraints(true);
  }

  private pauseVideoForTabChange(): void {
    const video = this.videoPlayerRef?.nativeElement;
    if (!video) return;

    video.pause();
    this.hlsPlayer?.stopLoad();
    this.isPlaying = false;
    this.stopVideoTimestamp();
  }

  getFilterSummary(): string {
    const parts: string[] = [];
    if (this.videoFilter.device) parts.push(`Equip: ${this.getSelectedDeviceName()}`);
    if (this.videoFilter.lane)   parts.push(`Pista: ${this.getSelectedLaneLabel()}`);
    if (this.videoFilter.date)   parts.push(`Data: ${this.videoFilter.date.toLocaleDateString('pt-BR')}`);
    if (this.activeTab?.id === 'relatorios' && this.videoFilter.startHour && this.videoFilter.endHour) {
      parts.push(`Horario: ${moment(this.videoFilter.startHour).format('HH:mm')} - ${moment(this.videoFilter.endHour).format('HH:mm')}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'Nenhum filtro aplicado';
  }

  private formatAppliedVideoFilterSummary(startDate: Date, endDate: Date): string {
    const parts: string[] = [];
    if (this.videoFilter.device) parts.push(`Equip: ${this.getSelectedDeviceName()}`);
    if (this.videoFilter.lane) parts.push(`Pista: ${this.getSelectedLaneLabel()}`);
    parts.push(`Início: ${moment(startDate).format('DD/MM/YYYY HH:mm')}`);
    parts.push(`Fim: ${moment(endDate).format('DD/MM/YYYY HH:mm')}`);
    return parts.join(' | ');
  }

  private captureAppliedVideoFilterSummary(startDate: Date, endDate: Date): void {
    this.appliedVideoFilterSummary = this.formatAppliedVideoFilterSummary(startDate, endDate);
  }

  captureUploadedVideoSummaryIfReady(): void {
    if (this.activeTab?.id !== 'video' || !this.isUploadedVideo || this.appliedVideoFilterSummary || !this.canPlayVideo()) {
      return;
    }

    const startDate = this.getSelectedDateTime();
    if (!startDate) return;

    const videoWindow = this.getVideoWindowForEvent(startDate);
    this.captureAppliedVideoFilterSummary(videoWindow.startDate, videoWindow.endDate);
  }

  getContract(): void {
    const contratoSelecionado: any = this.userService.getCurrentContractAsList();
    if (!contratoSelecionado[0]?.id) return;

    this.videoFilter.contractId = contratoSelecionado[0]?.id;
    this.contracts = contratoSelecionado;

    this.getDevices();
  }

  getLanes(): void {
    if (this.videoFilter.contractId) {
      this.loadingLane = true;
      this.occurrenceService.getLanes(this.videoFilter.contractId, this.getSelectedDeviceName())
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response: any) => {
            this.lanes = this.normalizeLaneOptions(response);
            this.loadingLane = false;

            if (this.pendingUploadedLaneLabel) {
              const uploadedLaneLabel = this.pendingUploadedLaneLabel;
              const uploadedLane = this.lanes.find(
                lane => lane.label === uploadedLaneLabel
              );

              if (!uploadedLane) {
                this.pendingUploadedLaneLabel = null;
                this.uploadedVideoActive = false;
                this.videoLoaded = false;
                this.messageService.add({
                  key: 'toastError', severity: 'error',
                  summary: 'Pista não encontrada',
                  detail: `A pista "${uploadedLaneLabel}" do arquivo não está disponível para o equipamento.`
                });
                return;
              }

              this.videoFilter.lane = uploadedLane.id;
              this.pendingUploadedLaneLabel = null;
              this.playUploadedVideo();
            }
          },
          error: () => {
            this.loadingLane = false;
            this.pendingUploadedLaneLabel = null;
            this.uploadedVideoActive = false;
            this.videoLoaded = false;
          }
        });
    }
  }

  private normalizeLaneOptions(response: any): LaneOption[] {
    if (Array.isArray(response?.organCod)) {
      const laneIds = Array.isArray(response?.id)      ? response.id
                    : Array.isArray(response?.ids)     ? response.ids
                    : Array.isArray(response?.laneId)  ? response.laneId
                    : Array.isArray(response?.laneIds) ? response.laneIds
                    : [];

      return response.organCod.map((laneLabel: any, index: number) => ({
        id: this.extractLaneId(laneIds[index] ?? laneLabel) || String(index + 1),
        label: this.resolveLaneLabel(laneLabel)
      }));
    }

    const laneValues = Array.isArray(response) ? response : [];
    return laneValues.map((lane: any, index: number) => ({
      id: this.extractLaneId(lane) || String(index + 1),
      label: this.resolveLaneLabel(lane)
    }));
  }

  private extractLaneId(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      const id = value.id ?? value.laneId ?? value.laneLabel ?? value.idFaixa ?? value.value;
      if (id !== null && id !== undefined && String(id).trim() !== '') {
        return String(id).trim();
      }
      const text = value.organCode ?? value.organCod ?? value.label ?? value.name ?? value.description;
      return text !== null && text !== undefined ? String(text).trim() : '';
    }
    return String(value).trim();
  }

  private resolveLaneLabel(lane: any): string {
    if (typeof lane === 'object' && lane !== null) {
      const label = lane.label ?? lane.name ?? lane.description ?? lane.organCod ?? lane.organCode ?? lane.id;
      return String(label ?? '').trim();
    }

    return String(lane ?? '').trim();
  }

  private getSelectedLaneId(): string {
    return this.extractLaneId(this.videoFilter.lane);
  }

  private getSelectedLaneLabel(): string {
    const lane = this.videoFilter.lane;
    if (!lane) return '';
    return this.lanes.find(l => l.id === lane)?.label ?? lane;
  }

  private getLaneLabel(id: any): string {
    if (id === null || id === undefined || String(id).trim() === '') return '';
    return this.lanes.find(l => l.id === String(id))?.label ?? String(id);
  }

  private getDeviceNameById(deviceId: string | number | null | undefined): string {
    if (deviceId === null || deviceId === undefined || String(deviceId).trim() === '') {
      return '';
    }

    const normalizedDeviceId = String(deviceId).trim();
    return this.devices.find((device) => String(device.deviceId).trim() === normalizedDeviceId)?.deviceName ?? normalizedDeviceId;
  }

  private getSelectedDeviceName(): string {
    return this.getDeviceNameById(this.videoFilter.device);
  }

  private getSelectedContractName(): string {
    const contract = this.contracts?.[0] ?? this.userService.getCurrentContract();
    const contractName = contract?.name
      ?? contract?.description
      ?? contract?.identification
      ?? contract?.enterprise
      ?? this.videoFilter.contractId;

    return contractName ? String(contractName) : '-';
  }

  getDevices(): void {
    this.deviceService.getDeviceMetrics()
      .pipe(takeUntil(this._destroy$))
      .subscribe((response: any) => {
        response.forEach((item: any) => {
          if (!this.devices.some(r => r.deviceName === item.deviceName)) {
            this.devices.push({
              deviceId: String(item.deviceId ?? item.equipmentId ?? item.id ?? item.equipmentOriginId),
              deviceName: item.deviceName,
              equipmentIds: [item.equipmentOriginId, item.deviceId, item.equipmentId, item.id]
                .filter(id => id !== null && id !== undefined)
                .map(id => String(id).trim())
            });
          }
        });

        this.devices.sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName, 'pt-BR', {
            numeric: true,
            sensitivity: 'base'
          })
        );
      });
  }

  onDeviceChange(): void {
    if (this.areFiltersLocked) return;

    this.uploadedVideoWindow = null;
    this.appliedVideoFilterSummary = null;
    this.videoFilter.lane = '';
    this.lanes = [];
    this.getLanes();
  }

  onVideoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
  
    if (!file) return;
  
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      this.messageService.add({
        key: 'toastError', severity: 'warn',
        summary: 'Arquivo inválido',
        detail: 'Selecione um arquivo no formato .mp4.'
      });
      return;
    }
  
    this.stopVideo();
    this.revokeLocalVideoUrl();
    this.resetVideoFiltersForNewUpload();
  
    this.uploadedVideoActive = true;
    this.uploadedMetadataAutoFilled = false;
    this.uploadedVideoWindow = null;
    this.pendingUploadedLaneLabel = null;
    this.localVideoUrl = URL.createObjectURL(file);
    this.videoLoaded = true;
    this.isPlaying = false;
    this.cdr.detectChanges();
  
    const metadata = this.parseUploadedVideoFileName(file.name);
    const device = metadata
      ? this.devices.find(item => this.isMatchingDevice(item, metadata.equipmentId))
      : undefined;
  
    if (!metadata || !device) {
      this.beginManualUploadedVideoEntry();
      return;
    }
  
    this.resolveUploadedVideoLane(device, metadata);
  }

  formatDuration(duration: number): string {
    const totalSeconds = Math.round(duration * 60);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  private resolveUploadedVideoLane(device: DeviceOption, metadata: UploadedVideoMetadata): void {
    this.loadingLane = true;
    this.occurrenceService.getLanes(this.videoFilter.contractId, device.deviceName)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response: any) => {
          this.lanes = this.normalizeLaneOptions(response);
          this.loadingLane = false;
  
          const uploadedLane = this.lanes.find(lane => lane.label === metadata.laneLabel);
  
          if (!uploadedLane) {
            this.beginManualUploadedVideoEntry();
            return;
          }
  
          this.uploadedMetadataAutoFilled = true;
          this.videoFilter.device = String(device.deviceId);
          this.videoFilter.date = new Date(metadata.startDate);
          this.videoFilter.startHour = new Date(metadata.startDate);
          this.videoFilter.endHour = new Date(metadata.endDate);
          this.videoFilter.duration = metadata.durationMinutes;
          this.uploadedVideoWindow = {
            startDate: new Date(metadata.startDate),
            endDate: new Date(metadata.endDate)
          };
          this.videoFilter.lane = uploadedLane.id;
          this.captureAppliedVideoFilterSummary(metadata.startDate, metadata.endDate);
          this.playUploadedVideo();
        },
        error: () => {
          this.loadingLane = false;
          this.beginManualUploadedVideoEntry();
        }
      });
  }

  private beginManualUploadedVideoEntry(): void {
    this.uploadedMetadataAutoFilled = false;
    this.pendingUploadedLaneLabel = null;
    this.uploadedVideoWindow = null;
    this.appliedVideoFilterSummary = null;
    this.messageService.add({
      key: 'toastError', severity: 'warn',
      summary: 'Preenchimento manual necessário',
      detail: 'Não foi possível reconhecer os dados do vídeo pelo nome do arquivo. Preencha os filtros manualmente antes de salvar a contagem.'
    });
    this.playUploadedVideo();
  }

  private resetVideoFiltersForNewUpload(): void {
    const contractId = this.videoFilter.contractId;
    this.videoFilter = new EquipmentVideoFilterModel();
    this.videoFilter.contractId = contractId;
    this.lanes = [];
  }

  private parseUploadedVideoFileName(fileName: string): UploadedVideoMetadata | null {
    const match =
      /^(.+)_([^_]+)_(\d{8})_(\d{2}-\d{2})_ate_(\d{2}-\d{2})_(dia|noite)\.mp4$/i.exec(fileName);
    if (!match) return null;
  
    const [, hostName, laneSegment, dateStr, startTimeStr, endTimeStr] = match;
  
    const startDate = this.parseUploadedVideoDate(dateStr, startTimeStr);
    let endDate = this.parseUploadedVideoDate(dateStr, endTimeStr);
  
    if (!startDate.isValid() || !endDate.isValid()) return null;
  
    // End time can be on the following day (e.g. a window crossing midnight).
    if (!endDate.isAfter(startDate)) {
      endDate = endDate.add(1, 'day');
    }
  
    return {
      equipmentId: hostName,
      laneLabel: laneSegment.slice(-2),
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      durationMinutes: endDate.diff(startDate, 'minutes', true)
    };
  }

  private parseUploadedVideoDate(dateStr: string, timeStr: string): moment.Moment {
    return moment(`${dateStr} ${timeStr}`, 'YYYYMMDD HH-mm', true);
  }
  
  private isMatchingDevice(device: DeviceOption, equipmentId: string): boolean {
    return device.deviceName?.trim().toLowerCase() === equipmentId.trim().toLowerCase();
  }
  

  private hasValidDate(): boolean {
    return (
      this.videoFilter.date instanceof Date &&
      !isNaN(this.videoFilter.date.getTime())
    );
  }

  private hasValidTime(): boolean {
    return (
      this.videoFilter.startHour instanceof Date &&
      !isNaN(this.videoFilter.startHour.getTime())
    );
  }

  private hasValidEndTime(): boolean {
    return (
      this.videoFilter.endHour instanceof Date &&
      !isNaN(this.videoFilter.endHour.getTime())
    );
  }

  canPlayVideo(): boolean {
    return (
      this.hasValidDate() &&
      this.hasValidTime() &&
      !!this.videoFilter.lane &&
      !!this.videoFilter.device &&
      !!this.videoFilter.duration
    );
  }

  canSaveCount(): boolean {
    return (
      this.videoLoaded &&
      this.canPlayVideo() &&
      !!this.videoFilter.lane &&
      !this.savingCount
    );
  }

  canGenerateReport(): boolean {
    return (
      this.hasValidDate() &&
      this.hasValidTime() &&
      this.hasValidEndTime() &&
      !!this.videoFilter.device &&
      !!this.videoFilter.lane &&
      !this.loadingReports
    );
  }

  clearFilter(): void {
    const contractId = this.videoFilter.contractId;
    this.videoFilter = new EquipmentVideoFilterModel();
    this.videoFilter.contractId = contractId;
    this.stopVideo();
    this.revokeLocalVideoUrl();
    this.uploadedVideoActive = false;
    this.uploadedMetadataAutoFilled = false;
    this.uploadedVideoWindow = null;
    this.pendingUploadedLaneLabel = null;
    this.videoLoaded     = false;
    this.isPlaying       = false;
    this.generatedReportRows = [];
    this.reportRecords = [];
    this.reportGenerated = false;
    this.savingCount     = false;
    this.stopVideoTimestamp();
    this.passageCount   = 0;
    this.lastAction     = null;
    this.counterHistory = [];
    this.appliedVideoFilterSummary = null;
  
    if (this.activeTab?.id === 'relatorios') {
      this.initializeReportTimeFields();
    }
  }

  onStartHourChange(selectedTime: Date): void {
    if (this.areFiltersLocked) return;

    if (this.activeTab?.id === 'relatorios') {
      this.videoFilter.startHour = selectedTime;
      this.enforceReportDateTimeConstraints(true);
      return;
    }

    if (!selectedTime || !this.videoFilter.date) return;

    const selectedDateTime = new Date(this.videoFilter.date);
    selectedDateTime.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
    this.videoFilter.startHour = selectedDateTime;

    this.videoFilter.endHour = this.getVideoWindowForEvent(selectedDateTime).endDate;
    this.captureUploadedVideoSummaryIfReady();
  }

  onEndHourChange(selectedTime: Date): void {
    if (this.areFiltersLocked) return;

    this.videoFilter.endHour = selectedTime;
    this.enforceReportDateTimeConstraints(false);
    this.captureUploadedVideoSummaryIfReady();
  }

  onStartHourClear(): void {
    if (this.areFiltersLocked) return;

    this.videoFilter.startHour = null;
    this.videoFilter.endHour   = null;
  }

  onDateClear(): void {
    if (this.areFiltersLocked) return;

    this.videoFilter.date = null;
    this.onStartHourClear();
  }

  onReportDateSelect(): void {
    if (this.areFiltersLocked) return;

    this.enforceReportDateTimeConstraints(true);
  }

  private initializeReportTimeFields(): void {
    if (!this.videoFilter.startHour) {
      this.videoFilter.startHour = new Date();
      this.videoFilter.startHour.setHours(0, 0, 0, 0);
    }

    if (!this.videoFilter.endHour) {
      this.videoFilter.endHour = new Date();
      this.videoFilter.endHour.setHours(23, 59, 59, 0);
    }

    this.enforceReportDateTimeConstraints(true);
  }

  private enforceReportDateTimeConstraints(isStart: boolean = true): void {
    this.clampReportDateToMaxDate();
    this.clampReportTimeIfToday(this.START_TIME);
    this.clampReportTimeIfToday(this.END_TIME);
    this.validateReportTimeRange(isStart);
    this.clampReportTimeIfToday(this.END_TIME);
  }

  private clampReportDateToMaxDate(): void {
    if (!this.videoFilter.date) return;

    const max = this.stripTime(this.maxDate);
    if (this.stripTime(this.videoFilter.date) > max) {
      this.videoFilter.date = new Date(max);
    }
  }

  private clampReportTimeIfToday(field: 'start' | 'end'): void {
    if (!this.shouldLimitReportHour(field)) return;

    const currentValue = field === this.START_TIME ? this.videoFilter.startHour : this.videoFilter.endHour;
    if (!currentValue) return;

    const now = this.getNowWithoutSeconds();
    const timeValue = this.normalizeTimeValue(currentValue);
    const candidate = new Date(now);
    candidate.setHours(timeValue.getHours(), timeValue.getMinutes(), 0, 0);

    if (candidate > now) {
      if (field === this.START_TIME) {
        this.videoFilter.startHour = new Date(now);
        return;
      }

      this.videoFilter.endHour = new Date(now);
    }
  }

  getLimitClass(field: 'start' | 'end'): any {
    return {
      'disable-hour-up': this.isHourAtMax(field),
      'disable-minute-up': this.isMinuteAtMax(field)
    };
  }

  getMaxTime(field: 'start' | 'end'): Date {
    return this.shouldLimitReportHour(field) ? this.getNowWithoutSeconds() : this.getEndOfDay();
  }

  private shouldLimitReportHour(_field: 'start' | 'end'): boolean {
    return !!this.videoFilter.date && this.isSameDay(this.videoFilter.date, new Date());
  }

  private isHourAtMax(field: 'start' | 'end'): boolean {
    if (!this.shouldLimitReportHour(field)) return false;

    const currentValue = field === this.START_TIME ? this.videoFilter.startHour : this.videoFilter.endHour;
    if (!currentValue) return false;

    const now = this.getNowWithoutSeconds();
    const value = this.normalizeTimeValue(currentValue);

    return value.getHours() >= now.getHours();
  }

  private isMinuteAtMax(field: 'start' | 'end'): boolean {
    if (!this.shouldLimitReportHour(field)) return false;

    const currentValue = field === this.START_TIME ? this.videoFilter.startHour : this.videoFilter.endHour;
    if (!currentValue) return false;

    const now = this.getNowWithoutSeconds();
    const value = this.normalizeTimeValue(currentValue);

    return value.getHours() === now.getHours() && value.getMinutes() >= now.getMinutes();
  }

  private validateReportTimeRange(isStart: boolean = true): void {
    if (!this.videoFilter.date || !this.videoFilter.startHour || !this.videoFilter.endHour) return;

    const rawStartHour = this.normalizeTimeValue(this.videoFilter.startHour);
    const rawEndHour = this.normalizeTimeValue(this.videoFilter.endHour);
    const startHour = new Date(this.videoFilter.date);
    const endHour = new Date(this.videoFilter.date);

    startHour.setHours(rawStartHour.getHours(), rawStartHour.getMinutes(), 0, 0);
    endHour.setHours(rawEndHour.getHours(), rawEndHour.getMinutes(), 0, 0);

    if (startHour <= endHour) return;

    if (isStart) {
      const newEnd = new Date(startHour.getTime() + 60 * 60 * 1000);
      if (newEnd.getDate() !== startHour.getDate()) {
        newEnd.setHours(23, 59, 0, 0);
      }
      this.videoFilter.endHour = newEnd;
      return;
    }

    const newStart = new Date(endHour.getTime() - 60 * 60 * 1000);
    if (newStart.getDate() !== endHour.getDate()) {
      newStart.setHours(0, 0, 0, 0);
    }
    this.videoFilter.startHour = newStart;
  }

  private normalizeTimeValue(value: Date | string): Date {
    if (value instanceof Date) {
      return value;
    }

    const parsed = new Date(`1970-01-01T${value}`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private stripTime(value: Date): Date {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private getNowWithoutSeconds(): Date {
    const now = new Date();
    now.setSeconds(0, 0);
    return now;
  }

  private getEndOfDay(): Date {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
  }

  private getSelectedDateTime(): Date | null {
    if (!this.hasValidDate() || !this.hasValidTime()) return null;

    const date = this.videoFilter.date as Date;
    const time = this.videoFilter.startHour as Date;
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);

    return selectedDateTime;
  }

  private getVideoWindowForEvent(dataHora: Date): { startDate: Date; endDate: Date } {
    if (this.uploadedVideoWindow) {
      return {
        startDate: new Date(this.uploadedVideoWindow.startDate),
        endDate: new Date(this.uploadedVideoWindow.endDate)
      };
    }

    const startDate = new Date(dataHora);
    const endDate = moment(dataHora).add(this.videoFilter.duration, 'minutes').toDate();
    return { startDate, endDate };
  }

  incrementCount(): void {
    if (!this.videoLoaded) return;
    this.passageCount++;
    this.lastAction = 'inc';
    this.triggerPulse();
    this.addHistory(this.passageCount, '+ 1')
  }

  decrementCount(): void {
    if (!this.videoLoaded || this.passageCount <= 0) return;
    this.passageCount--;
    this.lastAction = 'dec';
    this.triggerPulse();
    this.addHistory(this.passageCount,'- 1')
  }

  resetCount(): void {
    if (!this.videoLoaded || this.passageCount === 0) return;
    this.passageCount = 0;
    this.addHistory(this.passageCount, 'x0')
    this.lastAction   = 'zero';
  }

  adjustPlaybackRate(direction: 1 | -1): void {
    const newIndex = this.currentSpeedIndex + direction
    if (newIndex < 0 || newIndex >= this.speedOptions.length) return;

    this.currentSpeedIndex = newIndex;
    this.setPlaybackRate(this.speedOptions[newIndex])
  }

  setPlaybackRate(rate: number): void {
    this.videoSpeed = rate;
    this.videoPlayerRef.nativeElement.playbackRate = rate;
  }

  saveCount(): void {
    const dataHora = this.getSelectedDateTime();
  
    if (!dataHora || !this.videoFilter.device || !this.videoFilter.lane || !this.videoFilter.duration) {
      this.messageService.add({
        key: 'toastError',
        severity: 'warn',
        summary: 'Campos obrigatórios',
        detail: 'Equipamento, pista, dia, hora e duração precisam estar preenchidos antes de salvar a contagem.'
      });
      return;
    }
  
    const userId: number = this.userService.user?.id ?? 0;
    const videoWindow = this.getVideoWindowForEvent(dataHora);
  
    const payload: SavePassageCountPayload = {
      idEquipamento: String(this.videoFilter.device),
      laneLabel: this.getSelectedLaneLabel(),
      idOperador: String(userId),
      dataHora: moment().format('YYYY-MM-DDTHH:mm:ss'),
      dataHoraInicioVideo: moment(videoWindow.startDate).format('YYYY-MM-DDTHH:mm:ss'),
      dataHoraFimVideo: moment(videoWindow.endDate).format('YYYY-MM-DDTHH:mm:ss'),
      contagemTotal: this.passageCount
    };
  
    this.savingCount = true;
    this.equipmentVideoService.savePassageCount(payload)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.savingCount = false;
          this.messageService.add({
            key: 'toastSuccess', severity: 'success',
            summary: 'Contagem salva', detail: `${this.passageCount} passagem(ns) registrada(s) com sucesso.`
          });
        },
        error: () => {
          this.savingCount = false;
          this.messageService.add({
            key: 'toastError', severity: 'error',
            summary: 'Erro ao salvar', detail: 'Não foi possível salvar a contagem. Tente novamente.'
          });
        }
      });
  }

  private triggerPulse(): void {
    this.counterPulse = true;
    setTimeout(() => (this.counterPulse = false), 200);
  }

  private addHistory(value: number, action: string): void {
    const videoTime = this.videoPlayerRef?.nativeElement?.currentTime ?? null;

    let time = '--:--';
    if (videoTime !== null) {
      const mins = Math.floor(videoTime / 60).toString().padStart(2, '0');
      const secs = Math.floor(videoTime % 60).toString().padStart(2, '0');
      time = `${mins}:${secs}`;
    }

    this.counterHistory.unshift({ time, action, videoTime, count: value });
  }


  get formattedCount(): string {
    return String(this.passageCount).padStart(3, '0');
  }

  get countColorClass(): string {
    if (this.lastAction === 'inc') return 'count-green';
    if (this.lastAction === 'dec') return 'count-red';
    return 'count-zero';
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isPlaying) return;

    const tag = (event.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    switch (event.key) {
      case 'ArrowUp':    event.preventDefault(); this.incrementCount(); break;
      case 'ArrowDown':  event.preventDefault(); this.decrementCount(); break;
      case 'r':
      case 'R':          event.preventDefault(); this.resetCount();     break;
      case '_':
      case '-':          event.preventDefault(); this.adjustPlaybackRate(-1); break;
      case '=':
      case '+':          event.preventDefault(); this.adjustPlaybackRate(1);  break;
    }
  }

  togglePlay(): void {
    const video = this.videoPlayerRef?.nativeElement;
    if (!video) return;
    this.isPlaying ? video.pause() : video.play().catch(() => {});
  }

  onVideoEnded(): void {
    this.finishVideoPlayback();
  }

  onVideoDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleFullscreen();
  }

  replayAt(item: { videoTime: number | null; count: number }): void {
    const video = this.videoPlayerRef?.nativeElement;
    if (!video || item.videoTime === null) return;

    video.currentTime = item.videoTime;
    this.passageCount = item.count;
    this.lastAction   = null;
    this.triggerPulse();

    video.play().catch(() => {});
  }

  toggleFullscreen(): void {
    const container = this.videoContainerRef?.nativeElement;
    if (!container) return;

    const doc  = document as any;
    const isFs = doc.fullscreenElement || doc.webkitFullscreenElement;

    isFs
      ? doc.exitFullscreen?.()
      : container.requestFullscreen?.();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement;

    const container = this.videoContainerRef?.nativeElement;
    if (container) {
      container.classList.toggle('video-fullscreen', this.isFullscreen);
    }
  }

  private startVideoTimestamp(): void {
    this.stopVideoTimestamp();
    this.updateTimestamp();
    this.videoInterval = setInterval(() => this.updateTimestamp(), 500);
  }

  private stopVideoTimestamp(): void {
    if (this.videoInterval) {
      clearInterval(this.videoInterval);
      this.videoInterval = null;
    }
  }

  private updateTimestamp(): void {
    const video = this.videoPlayerRef?.nativeElement;
    if (!video) return;
    const t = video.currentTime;
    const m = Math.floor(t / 60).toString().padStart(2, '0');
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    this.videoTimestamp = `${m}:${s}`;

    if (this.shouldFinishVideoPlayback(video)) {
      this.finishVideoPlayback();
    }
  }

  private stopVideo(): void {
    this.videoPlaybackEnded = false;
    this.videoStreamComplete = false;

    if (this.hlsPlayer) {
      this.hlsPlayer.destroy();
      this.hlsPlayer = null;
    }

    if (this.hlsSessionId && this.hlsEquipmentId) {
      const sessionId = this.hlsSessionId;
      const equipmentId = this.hlsEquipmentId;
      this.hlsSessionId = null;
      this.hlsEquipmentId = null;
      this.equipmentVideoService.cancelEquipmentVideoHls(equipmentId, sessionId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({ error: () => undefined });
    }
    const video = this.videoPlayerRef?.nativeElement;
    if (video) {
      video.onplay = null;
      video.onplaying = null;
      video.onpause = null;
      video.onerror = null;
      video.onloadstart = null;
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.oncanplay = null;
      video.oncanplaythrough = null;
      video.ondurationchange = null;
      video.ontimeupdate = null;
      video.onwaiting = null;
      video.onstalled = null;
      video.onabort = null;
      video.onsuspend = null;
      video.onemptied = null;
      video.removeAttribute('src');
      video.load();
    }
  }

  private revokeLocalVideoUrl(): void {
    if (!this.localVideoUrl) return;

    URL.revokeObjectURL(this.localVideoUrl);
    this.localVideoUrl = null;
  }

  getOccurrence(_pageEvent: any, _filters: any): void {
    if (!this.canPlayVideo()) return;

    if (this.localVideoUrl) {
      this.playUploadedVideo();
      return;
    }

    this.currentSpeedIndex = this.speedOptions.indexOf(1);
    this.videoSpeed = 1;

    const dataHora = this.getSelectedDateTime();
    if (!dataHora) return;

    const videoWindow = this.getVideoWindowForEvent(dataHora);
    this.captureAppliedVideoFilterSummary(videoWindow.startDate, videoWindow.endDate);
    this.counterHistory = [];
    this.passageCount = 0;
    this.lastAction = null;
    this.stopVideo();
    this.videoLoaded = false;
    this.isPlaying   = false;''
    this.videoPlaybackEnded = false;
    this.videoStreamComplete = false;
    this.loading     = true;
    this.stopVideoTimestamp();

    this.videoLoaded = true;
    this.cdr.detectChanges();
    const video = this.videoPlayerRef?.nativeElement;
    if (!video || (!Hls.isSupported() && !video.canPlayType('application/vnd.apple.mpegurl'))) {
      this.loading = false;
      this.videoLoaded = false;
      this.messageService.add({
        key: 'toastError', severity: 'error',
        summary: 'Não suportado', detail: 'Seu navegador não suporta reprodução HLS.'
      });
      return;
    }

    this.equipmentVideoService.startEquipmentVideoHls({
      deviceId: String(this.videoFilter.device),
      laneId: String(this.videoFilter.lane),
      startDate: videoWindow.startDate,
      duration:  Number(this.videoFilter.duration)
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: ({ sessionId, playlistUrl }) => {
          this.hlsSessionId = sessionId;
          this.hlsEquipmentId = String(this.videoFilter.device);
          this.videoLoaded = true;
          this.loading = false;
          this.bindVideoEvents(video);
          this.setPlaybackRate(1);
          this.attachHlsSource(video, playlistUrl);
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.videoLoaded = false;
          this.isPlaying = false;
          this.messageService.add({
            key: 'toastError', severity: 'error',
            summary: 'Erro ao iniciar vídeo',
            detail: 'Não foi possível iniciar a reprodução HLS.'
          });
          console.error('[EquipmentVideo][HLS] Could not start session', error);
        }
      });

  }

  private attachHlsSource(video: HTMLVideoElement, playlistUrl: string): void {
    if (Hls.isSupported()) {
      const token = this.userService.getToken()?.trim();
      this.hlsPlayer = new Hls({
        xhrSetup: (xhr: XMLHttpRequest) => {
          if (token) xhr.setRequestHeader('Authorization', token);
        }
      });

      this.hlsPlayer.on(Hls.Events.MANIFEST_PARSED, () => video.pause());
      this.hlsPlayer.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
        this.videoStreamComplete = data?.details?.live === false;
        if (this.shouldFinishVideoPlayback(video)) {
          this.finishVideoPlayback();
        }
      });
      this.hlsPlayer.on(Hls.Events.ERROR, (_event, data) => {
        console.error('[EquipmentVideo][HLS] Playback error', data);
        if (!data.fatal || !this.hlsPlayer) return;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          this.hlsPlayer.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          this.hlsPlayer.recoverMediaError();
        } else {
          this.isPlaying = false;
          this.stopVideoTimestamp();
        }
      });

      this.hlsPlayer.attachMedia(video);
      this.hlsPlayer.loadSource(playlistUrl);
      return;
    }

    video.src = playlistUrl;
    video.load();
    video.pause();
  }

  private playUploadedVideo(): void {
    if (!this.localVideoUrl || !this.videoPlayerRef?.nativeElement) return;
  
    const video = this.videoPlayerRef.nativeElement;
    this.currentSpeedIndex = this.speedOptions.indexOf(1);
    this.videoSpeed = 1;
    this.counterHistory = [];
    this.passageCount = 0;
    this.lastAction = null;
    this.stopVideo();
    this.videoLoaded = true;
    this.loading = false;
    this.videoStreamComplete = true;
    this.bindVideoEvents(video);
    this.setPlaybackRate(1);
    video.src = this.localVideoUrl;
    video.load();
    video.pause();
    this.cdr.detectChanges();
  }

  private bindVideoEvents(video: HTMLVideoElement): void {
    video.onplay = () => {
      this.isPlaying = true;
      this.startVideoTimestamp();
    };
    video.onplaying = () => {
      this.isPlaying = true;
      this.videoPlaybackEnded = false;
    };
    video.onpause = () => {
      this.isPlaying = false;
      this.stopVideoTimestamp();
    };
    video.onerror = () => {
      this.isPlaying = false;
      this.stopVideoTimestamp();
      this.messageService.add({
        key: 'toastError', severity: 'error',
        summary: 'Erro ao reproduzir vídeo',
        detail: 'Não foi possível reproduzir o arquivo de vídeo.'
      });
    };
    video.ontimeupdate = () => {
      if (this.shouldFinishVideoPlayback(video)) {
        this.finishVideoPlayback();
      }
    };
    video.onwaiting = () => {
      if (this.shouldFinishVideoPlayback(video)) {
        this.finishVideoPlayback();
      }
    };
    video.onstalled = () => {
      if (this.shouldFinishVideoPlayback(video)) {
        this.finishVideoPlayback();
      }
    };
  }

  downloadVideo(): void {
    if (!this.canPlayVideo() || this.downloadingVideo) return;

    const dataHora = this.getSelectedDateTime();
    if (!dataHora) return;

    const videoWindow = this.getVideoWindowForEvent(dataHora);
    if (!this.validateDownloadWindow(videoWindow)) return;

    const filters = {
      deviceId: String(this.videoFilter.device),
      laneId: String(this.videoFilter.lane),
      startDate: videoWindow.startDate,
      duration:  Number(this.videoFilter.duration)
    };

    this.downloadProgress = 0;
    this.downloadedBytes = 0;
    this.downloadedBytesLabel = '';
    this.downloadProgressAvailable = false;
    this.downloadPhase = 'PENDING';
    this.downloadCancel$ = new Subject<void>();
    this.activeDownloadJobId = null;
    this.downloadingVideo = true;

    this.equipmentVideoService.startVideoDownload(filters)
      .pipe(takeUntil(this._destroy$), takeUntil(this.downloadCancel$))
      .subscribe({
        next: (status) => {
          this.activeDownloadJobId = status.jobId;
          this.updateDownloadStatus(status);
          if (status.state === 'READY') {
            this.fetchDownloadResult(String(this.videoFilter.device), status.jobId);
          } else if (status.state === 'FAILED' || status.state === 'CANCELED') {
            this.handleDownloadTerminalStatus(status);
          } else {
            this.pollDownloadStatus(String(this.videoFilter.device), status.jobId);
          }
        },
        error: (error) => this.handleDownloadHttpError(error)
      });
  }

  cancelDownload(): void {
    if (!this.downloadingVideo) return;

    const jobId = this.activeDownloadJobId;
    const equipmentId = String(this.videoFilter.device);

    this.downloadCancel$.next();
    this.downloadCancel$.complete();
    this.downloadCancel$ = new Subject<void>();
    this.activeDownloadJobId = null;

    if (jobId) {
      this.equipmentVideoService.cancelVideoDownload(equipmentId, jobId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          error: () => undefined
        });
    }

    this.ngZone.run(() => {
      this.downloadingVideo = false;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.downloadedBytesLabel = '';
      this.downloadProgressAvailable = false;
      this.downloadPhase = '';
      this.messageService.add({
        key: 'toastSuccess', severity: 'info',
        summary: 'Download cancelado', detail: 'O download do vídeo foi cancelado.'
      });
      this.cdr.detectChanges();
    });
  }

  private pollDownloadStatus(equipmentId: string, jobId: string): void {
    interval(1500)
      .pipe(
        startWith(0),
        switchMap(() => this.equipmentVideoService.getVideoDownloadStatus(equipmentId, jobId).pipe(
          retry({ count: 2, delay: 500 })
        )),
        takeWhile(status => status.state !== 'READY' && status.state !== 'FAILED' && status.state !== 'CANCELED', true),
        takeUntil(this._destroy$),
        takeUntil(this.downloadCancel$),
        catchError(() => {
          this.handleDownloadError('Não foi possível consultar o status do download. Tente novamente.');
          return throwError(() => new Error('Video download status failed'));
        })
      )
      .subscribe({
        next: (status) => {
          this.activeDownloadJobId = status.jobId;
          this.updateDownloadStatus(status);
          if (status.state === 'READY') {
            this.fetchDownloadResult(equipmentId, jobId);
          } else if (status.state === 'FAILED' || status.state === 'CANCELED') {
            this.handleDownloadTerminalStatus(status);
          }
        },
        error: () => undefined
      });
  }

  private fetchDownloadResult(equipmentId: string, jobId: string): void {
    this.ngZone.run(() => {
      this.downloadPhase = 'TRANSFERRING_RESULT';
      this.downloadProgress = Number(Math.max(this.downloadProgress, 90).toFixed(2));
      this.downloadProgressAvailable = true;
      this.cdr.detectChanges();
    });
  
    this.equipmentVideoService.getVideoDownloadResult(equipmentId, jobId)
      .pipe(takeUntil(this._destroy$), takeUntil(this.downloadCancel$), retry({ count: 2, delay: 500 }))
      .subscribe({
        next: (response) => this.triggerDownload(
          response.body,
          this.getDownloadFileName(response.headers.get('Content-Disposition'))
        ),
        error: (error) => this.handleDownloadHttpError(error)
      });
  }

  private updateDownloadStatus(status: VideoDownloadStatus): void {
    this.ngZone.run(() => {
      this.downloadPhase = status.state;
      this.downloadedBytes = status.bytesDownloadedFromNvr || 0;
      this.downloadedBytesLabel = this.formatBytes(this.downloadedBytes) + ' baixados';
      const terminalWithoutProgress = status.state === 'CANCELED' || status.state === 'FAILED';
      this.downloadProgressAvailable = !terminalWithoutProgress && status.percentComplete !== null;

      if (terminalWithoutProgress) {
        this.downloadProgress = 0;
        this.cdr.detectChanges();
        return;
      }

      const byteProgress = status.totalBytesExpected
        ? (this.downloadedBytes / status.totalBytesExpected) * 100
        : null;
      const reportedProgress = status.percentComplete ?? byteProgress;

      if (reportedProgress !== null) {
        this.downloadProgress = Math.max(
          this.downloadProgress,
          Math.min(100, reportedProgress)
        );
      }
      this.cdr.detectChanges();
    });
  }

  private handleDownloadTerminalStatus(status: VideoDownloadStatus): void {
    if (status.state === 'CANCELED') {
      this.handleDownloadError('O download do vídeo foi cancelado.');
      return;
    }

    if (this.isFfmpegUnavailableMessage(status.errorMessage)) {
      this.handleDownloadError('O vídeo está atualmente indisponível para download porque não pôde ser convertido para MP4.');
      return;
    }

    this.handleDownloadError(status.errorMessage || 'Não foi possível baixar o vídeo. Tente novamente.');
  }

  private handleDownloadHttpError(error: unknown): void {
    const errorMessage = this.extractDownloadErrorMessage(error);

    if (this.isFfmpegUnavailableMessage(errorMessage)) {
      this.handleDownloadError('O vídeo está atualmente indisponível para download porque não pôde ser convertido para MP4.');
      return;
    }

    this.handleDownloadError(errorMessage || undefined);
  }

  private extractDownloadErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') return null;

    const responseError = error as { error?: unknown; message?: unknown };
    const body = responseError.error;

    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      const bodyMessage = (body as { message?: unknown; errorMessage?: unknown }).message
        ?? (body as { errorMessage?: unknown }).errorMessage;
      if (typeof bodyMessage === 'string') return bodyMessage;
    }

    return typeof responseError.message === 'string' ? responseError.message : null;
  }

  private isFfmpegUnavailableMessage(message: string | null): boolean {
    return !!message && /ffmpeg failed to create mp4[\s\S]*exit code\s*=\s*1/i.test(message);
  }

  private validateDownloadWindow(videoWindow: { startDate: Date; endDate: Date }): boolean {
    const allowedEnd = new Date();
    const allowedStart = new Date(allowedEnd);
    allowedStart.setDate(allowedStart.getDate() - 5);

    const startsBeforeAllowedPeriod = videoWindow.startDate < allowedStart;
    const endsAfterAllowedPeriod = videoWindow.endDate > allowedEnd;

    if (!startsBeforeAllowedPeriod && !endsAfterAllowedPeriod) return true;

    this.messageService.add({
      key: 'toastError',
      severity: 'error',
      summary: 'Vídeo fora do período disponível',
      detail: `O período solicitado está fora do intervalo permitido para download/reprodução.`
    });
    return false;
  }

  private formatDownloadDate(date: Date): string {
    return moment(date).format('DD/MM/YYYY HH:mm');
  }

  private handleDownloadError(detail: string = 'Não foi possível baixar o vídeo. Tente novamente.'): void {
    this.ngZone.run(() => {
      this.downloadingVideo = false;
      this.activeDownloadJobId = null;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.downloadedBytesLabel = '';
      this.downloadProgressAvailable = false;
      this.downloadPhase = '';
      this.messageService.add({
        key: 'toastError', severity: 'error',
        summary: 'Erro ao baixar', detail
      });
      this.cdr.detectChanges();
    });
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = -1;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private triggerDownload(blob: Blob | null, fileName: string | null): void {
    if (!blob) {
      this.handleDownloadError();
      return;
    }

    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName || this.getVideoDownloadFileName();
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(downloadUrl);

    this.ngZone.run(() => {
      this.downloadingVideo = false;
      this.activeDownloadJobId = null;
      this.downloadProgress = 100;
      this.downloadProgressAvailable = true;
      this.downloadedBytes = 0;
      this.downloadedBytesLabel = '';
      this.downloadPhase = '';
      this.cdr.detectChanges();
    });
  }

  private getDownloadFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
    if (encodedName) return decodeURIComponent(encodedName);

    const name = /filename="?([^";]+)"?/i.exec(contentDisposition)?.[1];
    return name?.trim() || null;
  }

  private getVideoDownloadFileName(): string {
    const equipmentName = this.getSelectedDeviceName() || 'equipamento';
    const laneLabel = this.getSelectedLaneLabel() || 'pista';
    const dataHora = this.getSelectedDateTime();
    const videoWindow = dataHora ? this.getVideoWindowForEvent(dataHora) : null;
    const start = moment(videoWindow?.startDate ?? dataHora ?? new Date());
    const end = moment(videoWindow?.endDate ?? dataHora ?? new Date());
  
    const laneSegment = laneLabel.length >= 2 ? laneLabel.slice(-2) : laneLabel.padStart(2, '0');
    const period = this.getRunPeriod(start);
  
    return `${equipmentName}_${laneSegment}_${start.format('YYYYMMDD')}_${start.format('HH-mm')}_ate_${end.format('HH-mm')}_${period}.mp4`;
  }

  private getRunPeriod(date: moment.Moment): 'dia' | 'noite' {
    const hour = date.hour();
    return hour >= 6 && hour < 18 ? 'dia' : 'noite';
  }

  private shouldFinishVideoPlayback(video: HTMLVideoElement): boolean {
    if (this.videoPlaybackEnded || video.paused || video.seeking) {
      return false;
    }

    const bufferedEnd = this.getBufferedEnd(video);
    return (
      this.videoStreamComplete &&
      bufferedEnd !== null &&
      video.currentTime >= bufferedEnd - this.videoEndToleranceSeconds
    );
  }

  private finishVideoPlayback(): void {
    const video = this.videoPlayerRef?.nativeElement;

    this.videoPlaybackEnded = true;
    this.isPlaying = false;
    this.stopVideoTimestamp();

    if (video && !video.paused) {
      video.pause();
    }

    this.hlsPlayer?.stopLoad();
  }

  private getBufferedEnd(video: HTMLVideoElement): number | null {
    const buffered = video.buffered;
    return buffered.length ? buffered.end(buffered.length - 1) : null;
  }

  generateReport(): void {
    if (!this.canGenerateReport()) {
      this.messageService.add({
        key: 'toastError',
        severity: 'warn',
        summary: 'Filtros obrigatorios',
        detail: 'Informe equipamento, pista, data e intervalo de horario para gerar o relatorio.'
      });
      return;
    }

    this.loadingReports = true;
    this.reportGenerated = false;
    this.generatedReportRows = [];
    this.reportRecords = [];

    const reportDateRange = this.getReportDateRange();
    if (!reportDateRange) {
      this.loadingReports = false;
      return;
    }

    this.equipmentVideoService
      .getReports(0, 20, {
      idEquipamento: String(this.videoFilter.device),
      laneLabel: String(this.videoFilter.lane),
      dataHoraInicio: reportDateRange.dataHoraInicio,
      dataHoraFim: reportDateRange.dataHoraFim
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          const records = this.extractReportRecords(response);

          this.reportRecords = records;
          this.generatedReportRows = this.buildGeneratedReportRows(records);
          this.reportGenerated = true;
          this.loadingReports = false;
          this.messageService.add({
            key: 'toastSuccess',
            severity: 'success',
            summary: 'Relatorio gerado',
            detail: 'Relatorio gerado com sucesso.'
          });
        },
        error: () => {
          this.loadingReports = false;
          this.messageService.add({
            key: 'toastError', severity: 'error',
            summary: 'Erro ao gerar relatorio',
            detail: 'Nao foi possivel gerar o relatorio. Tente novamente.'
          });
        }
      });
  }

  private getReportDateRange(): { dataHoraInicio: string; dataHoraFim: string } | null {
    if (!this.hasValidDate() || !this.hasValidTime() || !this.hasValidEndTime()) {
      return null;
    }

    const date = this.videoFilter.date as Date;
    const startTime = this.videoFilter.startHour as Date;
    const endTime = this.videoFilter.endHour as Date;
    const dataHoraInicio = new Date(date);
    const dataHoraFim = new Date(date);

    dataHoraInicio.setHours(startTime.getHours(), startTime.getMinutes(), startTime.getSeconds(), 0);
    dataHoraFim.setHours(endTime.getHours(), endTime.getMinutes(), endTime.getSeconds(), 0);

    return {
      dataHoraInicio: moment(dataHoraInicio).format('YYYY-MM-DDTHH:mm:ss'),
      dataHoraFim: moment(dataHoraFim).format('YYYY-MM-DDTHH:mm:ss')
    };
  }

  private extractReportRecords(response: ReportRecord[] | ReportPageResponse): ReportRecord[] {
    return Array.isArray(response) ? response : response?.content ?? [];
  }

  private buildGeneratedReportRows(records: ReportRecord[]): GeneratedReportRow[] {
    return records.map((report) => ({
      id: report.id,
      dataHora: this.formatReportDateTime(report.dataHora),
      dataHoraInicioVideo: this.formatReportDateTime(report.dataHoraInicioVideo),
      dataHoraFimVideo: this.formatReportDateTime(report.dataHoraFimVideo),
      idEquipamento: this.getDeviceNameById(report.idEquipamento),
      laneLabel: this.getLaneLabel(report.laneLabel),
      nomeOperador: (report.nomeOperador ?? '-'),
      contagemTotal: report.contagemTotal,
      contagemEsperada: report.contagemEsperada,
      selected: false
    }));
  }

  getReportFileName(): string {
    const equipment = this.videoFilter.device ? String(this.videoFilter.device) : 'equipamento';
    const lane = this.videoFilter.lane || 'pista';
    const date = this.videoFilter.date ? moment(this.videoFilter.date).format('YYYYMMDD') : moment().format('YYYYMMDD');

    return `relatorio_${equipment}_${lane}_${date}`;
  }

  exportReportCsv(): void {
    if (!this.generatedReportRows.length) {
      return;
    }

    const csvData = this.buildReportCsv();
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${this.getReportFileName()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  deleteReportCount(): void {
    const selectedIds = new Set(this.selectedReportRows.map(row => row.id));
    const recordsToDelete = this.reportRecords.filter(report => selectedIds.has(report.id));

    if (!recordsToDelete.length) {
      return;
    }

    this.loadingReports = true;
    this.equipmentVideoService.deletePassageCount(recordsToDelete.map(report => report.id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.reportRecords = this.reportRecords.filter(report => !selectedIds.has(report.id));
          this.generatedReportRows = this.generatedReportRows.filter(row => !selectedIds.has(row.id));
          this.loadingReports = false;
          this.messageService.add({
            key: 'toastSuccess',
            severity: 'success',
            summary: 'Contagens apagadas',
            detail: 'As contagens do relatório foram removidas com sucesso.'
          });
        },
        error: () => {
          this.loadingReports = false;
          this.messageService.add({
            key: 'toastError',
            severity: 'error',
            summary: 'Erro ao apagar',
            detail: 'Não foi possível apagar as contagens do relatório. Tente novamente.'
          });
        }
      });
  }

  exportReportPdf(): void {
    if (!this.generatedReportRows.length) {
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const head = [this.reportColumns.map((column) => column.header)];
    const body = this.generatedReportRows.map((row) =>
      this.reportColumns.map((column) => this.formatExportValue(row[column.field]))
    );

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(14);
    doc.text('Relatório de Contagem', pageWidth / 2, 14, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`Contrato: ${this.getSelectedContractName()}`, pageWidth / 2, 22, { align: 'center' });
    const tableStartY = this.drawReportMetadataGrid(doc, pageWidth, 30) + 8;

    autoTable(doc, {
      head,
      body,
      startY: tableStartY,
      theme: 'grid',
      margin: { left: 12, right: 12 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: 'center',
        valign: 'middle',
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.1
      },
      headStyles: {
        fillColor: [68, 114, 196],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'left' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'right' }
      },
      didParseCell: (data) => {
        if (data.section !== 'head') return;

        if (data.column.index === 3) {
          data.cell.styles.halign = 'left';
        }

        if (data.column.index === 6) {
          data.cell.styles.halign = 'right';
        }
      }
    });

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page++) {
      doc.setPage(page);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`Página ${page} de ${pageCount}`, pageWidth - 12, pageHeight - 8, { align: 'right' });
    }

    doc.save(`${this.getReportFileName()}.pdf`);
  }

  private drawReportMetadataGrid(doc: jsPDF, pageWidth: number, startY: number): number {
    const marginX = 14;
    const gap = 3;
    const columns = 3;
    const cellWidth = (pageWidth - marginX * 2 - gap * (columns - 1)) / columns;
    const cellHeight = 16;
    const rowGap = 3;
    const items = [
      { label: 'Equipamento', value: this.getSelectedDeviceName() || '-' },
      { label: 'Pista', value: this.getSelectedLaneLabel() || '-' },
      { label: 'Período', value: this.getReportPeriodLabel() },
      { label: 'Gerado por', value: this.getGeneratedByLabel() },
      { label: 'Gerado em', value: moment().format('DD/MM/YYYY HH:mm:ss') },
      { label: 'Registros', value: String(this.generatedReportRows.length) }
    ];

    items.forEach((item, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = marginX + column * (cellWidth + gap);
      const y = startY + row * (cellHeight + rowGap);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.rect(x, y, cellWidth, cellHeight, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(item.label.toUpperCase(), x + 3, y + 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.text(doc.splitTextToSize(item.value, cellWidth - 6).slice(0, 2), x + 3, y + 11);
    });

    doc.setFont('helvetica', 'normal');
    return startY + cellHeight * 2 + rowGap;
  }

  getReportColumnClass(field: keyof GeneratedReportRow): string {
    if (field === 'idEquipamento') {
      return 'text-left';
    }

    if (field === 'contagemTotal') {
      return 'text-right';
    }

    return 'text-center';
  }

  private getReportPeriodLabel(): string {
    if (!this.hasValidDate() || !this.hasValidTime() || !this.hasValidEndTime()) {
      return '-';
    }

    const date = this.videoFilter.date as Date;
    const startTime = this.videoFilter.startHour as Date;
    const endTime = this.videoFilter.endHour as Date;
    const startDateTime = new Date(date);
    const endDateTime = new Date(date);

    startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
    endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

    return `${moment(startDateTime).format('DD/MM/YYYY HH:mm')} - ${moment(endDateTime).format('HH:mm')}`;
  }

  private getGeneratedByLabel(): string {
    const user = this.userService.user;
    return user?.userName || user?.email || String(user?.id ?? '-');
  }

  private buildReportCsv(): string {
    const csvSeparator = ';';
    const header = this.reportColumns
      .map((column) => this.formatExportValue(column.header))
      .join(csvSeparator);
    const rows = this.generatedReportRows.map((row) => {
      return this.reportColumns
        .map((column) => this.formatExportValue(row[column.field]))
        .join(csvSeparator);
    });

    return [header, ...rows].join('\n');
  }

  private formatExportValue(value: string | number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value);
  }

  formatReportDateTime(dateTime: string): string {
    return dateTime ? moment(dateTime).format('DD/MM/YYYY HH:mm:ss') : '-';
  }
}
